import { useDispatch, useSelector } from 'react-redux';
import { NUMERIC_TYPES } from '../../../../constants';
import {
    CWTSchemaPropertySummary,
    CWTSchemaSection,
    CWTSchemaTable,
    FieldType as FieldTypeEnum,
} from '../../../../types/custom-worksheet-v2';
import { isCWTSchemaSection, isCWTSchemaTable } from '../../store/utils';
import { useEffect, useState } from 'react';
import { getReference } from '../../../../components/custom-worksheet/utils';
import { editSectionShowSummary, editTableShowSummary } from '../../store/slice';
import FieldTypeSelect from '../../../../components/field-type-select/FieldTypeSelect';
import SummaryRows from './SummaryRows';
import ToggleField from './ToggleField';
import Reconciliation from './reconciliation/Reconciliation';
import { selectSchema } from '../../store/selectors';

type TableOrSectionSummaryProps = {
    property: CWTSchemaSection | CWTSchemaTable;
};

function TableOrSectionSummary({ property }: TableOrSectionSummaryProps): JSX.Element {
    const dispatch = useDispatch();
    const schema = useSelector(selectSchema);

    const [fieldType, setFieldType] = useState<FieldTypeEnum | undefined>(undefined);

    useEffect(() => {
        if ('summary' in property) {
            setFieldType(property.summary?.properties[getReference(property.id)]?.type);
        }
    }, [property]);

    function handleToggle(): void {
        if (isCWTSchemaTable(property)) {
            dispatch(editTableShowSummary(property.id));
        }

        if (isCWTSchemaSection(property)) {
            // If property summary is being toggled off, set field type to undefined, else default to MONEY type
            setFieldType(property.summary ? undefined : FieldTypeEnum.MONEY);
            dispatch(editSectionShowSummary({ id: property.id }));
        }
    }

    return (
        <div className="flex flex-col gap-2">
            <ToggleField
                title="Summary Row"
                description={`Add a summary row at the bottom of the ${property.type.toLowerCase()}.`}
                defaultEnabled={Boolean(property.summary)}
                onToggle={handleToggle}
                disabledHint={
                    isCWTSchemaTable(property) && property.generatedFromFunction
                        ? 'Generated tables automatically generate a summary'
                        : undefined
                }
            />
            {Boolean(property.summary) && (
                <>
                    {isCWTSchemaTable(property) ? (
                        <SummaryRows table={property} />
                    ) : (
                        <div>
                            <FieldTypeSelect
                                autoFocus={false}
                                allowedTypes={NUMERIC_TYPES}
                                value={fieldType ? (fieldType as FieldTypeEnum) : undefined}
                                onChange={(val): void => {
                                    setFieldType(val.length === 0 ? FieldTypeEnum.MONEY : val[0]);
                                    dispatch(editSectionShowSummary({ id: property.id, type: val[0] }));
                                }}
                            />
                            {fieldType === FieldTypeEnum.MONEY && property.summary && (
                                <Reconciliation
                                    property={
                                        property.summary.properties[property.id.split('.').at(-1) as string] as CWTSchemaPropertySummary
                                    }
                                    schema={schema}
                                />
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default TableOrSectionSummary;
